import { createDuckPrefab } from "@/presentation/prefabs/actor/duck.js";
import { createWaterPrefab } from "@/presentation/prefabs/actor/water.js";
import { createDuckLineSlotPrefab } from "@/presentation/prefabs/actor/duckLineSlot.js";
import { sniperAnimation } from "@/presentation/renderers/animations/sniper.js";
import { getLineSlots, getLineActors } from "@/presentation/helpers/selectors.js";

const spawnLocation = {
    x: 960,
    y: 195
};
const exitLocation = {
    left:  { x: 100, y: 195 },
    right: { x: 1200, y: 195 },
};

// Setup invisible slots location for the line of fire
export function createDuckLineSlots(k) {
    const startX = 300;
    const startY = 195;
    const spacing = 110;

    for (let i = 0; i < 6; i++) {
        const position = k.vec2(startX + (i * spacing), startY)
        createDuckLineSlotPrefab(k, i, position);
    }
}

function createLineItem(k, card, position, index) {
    if (card.typeId === "WATER") {
        return createWaterPrefab(k, k.vec2(position.x, position.y), index);
    } else {
        return createDuckPrefab(k, card, position, index);
    }
}

// Render the initial object in the line of fire (duck || water)
export async function renderInitialLineOfFire(k, lineOfFireArray) {
    const lineSlots = getLineSlots(k);
    const SPEED = 300;

    await Promise.all(lineOfFireArray.map(async (card, index) => {
        await k.wait(index * 0.3);

        const newLineOfFireItem = createLineItem(k, card, k.vec2(spawnLocation.x, spawnLocation.y), index);
        const distance = 960 - lineSlots[index].pos.x;
        const duration = distance / SPEED;

        await newLineOfFireItem.slide(lineSlots[index].pos.x, duration);
    }));
}

export async function executeMultiKillSequence(k, kills, waterHits, cardTypeId, gameState) {
    const lineSlots = getLineActors(k);
    const totalAnimations = kills.length + waterHits.length;

    if (totalAnimations === 0) {
        console.warn("executeMultiKillSequence called without target", totalAnimations)
        return;
    }

    // Gather all targets
    const targetedIndexes = [
        ...kills.map(k => k.indexKilled),
        ...waterHits
    ];

    // Find the actual Prefab Actors
    const victims = targetedIndexes.map(index =>
        lineSlots.find(c => c.slotIndex === index)
    ).filter(v => v !== undefined);

    if (victims.length === 0) {
        console.warn("executeMultiKillSequence can't find victims in line of fire", victims)
        return;
    }

    await playWeaponSequence(k, cardTypeId, victims, gameState);
    await shiftLineOfFire(k, kills.map(k => k.indexKilled), gameState);
}

// Use correct animation depending on the card played. End when all victims trigger their animationEnd
export async function playWeaponSequence(k, cardTypeId, victims, gameState) {
    // Initial Weapon Sounds
    if (cardTypeId === "CANARDAGE") {
        k.play("shootgun", { volume: 1 });
    } else if(cardTypeId === "TIREUR_DELITE") {} // TIREUR_DELITE handle is own sound
    else {
        k.play("pistol_shoot", { volume: 0.5 });
    }

    // Weapon animation
    await Promise.all(victims.map(async (victim) => {
        if (cardTypeId === "TIREUR_DELITE") {
            // NOTE: This will show multiple sniper targets on screen at the same time
            await sniperAnimation(k, victim);

            if(!gameState.slotIsProtected(victim.slotIndex)) {
                await victim.hit();
                await k.wait(0.2);
            }
        } else {
            if (!gameState.slotIsProtected(victim.slotIndex)) {
                await victim.hit();
            }
        }
    }));
}

// Slide survivors to their new slots
export async function slideLineOfFire(k, entitiesToMoveIndices, shiftGap = 0, goRight = false, duration = 1.5) {
    const lineSlots = getLineSlots(k);
    const lineActors = getLineActors(k);

    const sorted = [...entitiesToMoveIndices].sort((a, b) => goRight ? b - a : a - b);

    await Promise.all(sorted.map(async (index) => {
        const lineActor = lineActors.find(c => c.slotIndex === index);
        if (!lineActor) return;

        const newSlotIndex = goRight ? lineActor.slotIndex + shiftGap : lineActor.slotIndex - shiftGap;
        const outOfBounds = goRight ? newSlotIndex > lineSlots.length - 1 : newSlotIndex < 0;

        lineActor.flipX = !goRight;
        if (outOfBounds) {
            await lineActor.slide(goRight ? exitLocation.right.x : exitLocation.left.x, duration);
            lineActor.destroy();
        } else {
            lineActor.slotIndex = newSlotIndex;
            await lineActor.slide(lineSlots[newSlotIndex].pos.x, duration);
        }
        lineActor.flipX = true;
    }));
}

// Fill empty slots with new entities
export async function spawnNewLineItems(k, slotIndicesToFill, gameState) {
    const lineSlots = getLineSlots(k);
    const spawnAnimationDuration = 0.5;

    if (slotIndicesToFill.length === 0) return;

    await Promise.all(slotIndicesToFill.map(async (slotToFill) => {
        const targetPos = lineSlots[slotToFill].pos;
        const newCardData = gameState.lineOfFire[slotToFill];
        if (!newCardData) return;

        const newItem = createLineItem(k, newCardData, k.vec2(spawnLocation.x, spawnLocation.y), slotToFill);
        await newItem.slide(targetPos.x, spawnAnimationDuration);
    }));
}

// Orchestrator — slide then spawn
export async function shiftLineOfFire(k, targetedIndexes, gameState) {
    // Filter protected duck
    targetedIndexes = targetedIndexes.filter(index => !gameState.slotIsProtected(index))
    if (targetedIndexes.length === 0) return;

    const maxKillIndex = Math.max(...targetedIndexes);
    const shiftGap = targetedIndexes.length;
    const lineOfFireIndexes = gameState.lineOfFire.map((_, i) => i);
    const slotsAfterKills = lineOfFireIndexes.filter(i => i > maxKillIndex);
    const emptySlots = lineOfFireIndexes.filter(i => i >= 6 - shiftGap);

    await slideLineOfFire(k, slotsAfterKills, shiftGap);
    await spawnNewLineItems(k, emptySlots, gameState);
}

export async function duckSwitch(k, duckFromIndex, duckToIndex) {
    const lineActors = getLineActors(k);
    const fromDuck = lineActors.find(c => c.slotIndex === duckFromIndex);
    const toDuck = lineActors.find(c => c.slotIndex === duckToIndex);

    if (!fromDuck || !toDuck) {
        console.warn("card not found", {duckFromIndex, duckToIndex});
        return;
    }

    const fromPos = fromDuck.pos.clone();
    const toPos = toDuck.pos.clone();

    fromDuck.slotIndex = duckToIndex;
    toDuck.slotIndex = duckFromIndex;

    await Promise.all([
        fromDuck.jumpTo(toPos, 0.6),
        toDuck.jumpTo(fromPos, 0.6),
    ]);
}

export async function duckBanzai(k, duckIndex, gameState) {
    const lineSlots = getLineSlots(k);
    const lineActors = getLineActors(k);
    const duck = lineActors.find(c => c.slotIndex === duckIndex);

    if (!duck) {
        console.warn("duck not found", { duckIndex });
        return;
    }

    const lineOfFireIndexes = gameState.lineOfFire.map((_, i) => i);
    const ducksToMove = lineOfFireIndexes.filter(i => i < duckIndex);

    await Promise.all([
        duck.jumpTo(lineSlots[0].pos, 0.6),
        slideLineOfFire(k, ducksToMove, 1, true)
    ]);

    duck.slotIndex = 0;
}

export async function clearLineOfFire(k) {
    const lineActors = getLineActors(k);

    await Promise.all(lineActors.map(async (lineActor) => {
        const distance = Math.abs(exitLocation.left.x - lineActor.pos.x);
        const duration = distance / 220;
        await lineActor.slide(exitLocation.left.x, duration);
        lineActor.destroy();
    }));
}

export async function protectDuck(k, index) {
    const lineSlots = getLineSlots(k);
    const slot = lineSlots[index];

    slot.protect();
}

export async function exposeDuck(k, index) {
    const lineSlots = getLineSlots(k);
    const slot = lineSlots[index];

    slot.expose();
}